import React, { useCallback, useMemo } from 'react';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { getTableColumns } from './columns';
import { SelectedCellInfo } from '../hooks';
import { FormMode } from '@/types/form';
import { useTaxRate } from '@/hooks';

interface ThueTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<MuaHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<MuaHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
}

const TAX_ACTION_ORDER = [
  'add',
  'delete',
  'copy',
  'paste',
  'moveUp',
  'moveDown',
  'export',
  'pin',
  'viewInventory',
  'viewReceipt'
] as const;
const noop = () => {};

export const ThueTab: React.FC<ThueTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange
}) => {
  const { taxRates } = useTaxRate();
  const columns = useMemo(() => getTableColumns(taxRates, onCellValueChange), [onCellValueChange, taxRates]);
  const handleRowId = useCallback((row: MuaHangRecord) => row?.uuid || '', []);
  const tableActions = useMemo(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: noop,
      pin: noop,
      viewInventory: noop,
      viewReceipt: noop
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <ErpInputTableV2
      autoHeight
      rows={rows}
      onRowClick={onRowClick as MuaHangRecord}
      onCellClick={onCellClick as MuaHangRecord}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={handleRowId}
      pasteTarget={selectedCell}
      actionsDisabled={formMode === 'view'}
      actionOrder={TAX_ACTION_ORDER as MuaHangRecord}
      actions={tableActions}
    />
  );
};
